import React, { useEffect, useRef, useState } from "react";

import { useSuspenseQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import { Link, NavLink, Outlet } from "react-router-dom-v5-compat";
import { twJoin } from "tailwind-merge";

import {
  SearchField,
  LinkButton,
  Price,
  RecordFeatureView,
  PriceLine,
} from "./Atoms";
import { OrderedOrderItems } from "./Pages/Cart/OrderedOrderItem";
import { PendingCartItem } from "./Pages/Cart/PendingCartItem";
import { getCartTotal } from "./Pages/Cart/selectors";
import OnlineOrderingDisabled from "./Pages/OnlineOrderingSchedule/OnlineOrderingDisabled";
import { SearchResults } from "./Pages/Sections/Section";
import { useSession } from "./Pages/Session/SessionContext";
import { getIsWishlist } from "./Pages/Session/sessionSelectors";
import { VenueOnlineConfigQueries } from "./Pages/VenueOnlineConfig/queries";
import { useVenue } from "./VenueContext";
import { getActiveSections, getPromotions } from "./menuSelectors";
import { useFetchMenu } from "./queries";
import type { Venue, Menu } from "./schema";
import { getVenueHasMobileOrdering } from "./venueSelectors";

interface MainPageProps {
  smallView?: boolean;
  isRootPage?: boolean;
}
const hasLogoImage = ({ logoImageUrl }: Venue) =>
  logoImageUrl && logoImageUrl != "/assets/missing.png";
export const LogoImage = () => {
  const venue = useVenue();

  if (!hasLogoImage(venue)) {
    return null;
  }
  return (
    <div className="aspect-h-1 aspect-w-1">
      <img
        className={classNames(
          "rounded-venue-brand bg-venue-primary object-cover",
        )}
        src={venue.logoImageUrl}
      />
    </div>
  );
};

export const SectionPill = ({
  isActive,
  name,
  small,
}: {
  isActive?: boolean;
  name: string;
  small?: boolean;
}) => {
  const venueBrand = useVenue().venueBrand;
  return (
    <div
      className={twJoin(
        isActive
          ? "border-venue-primary bg-venue-primary text-venue-tertiary"
          : "border-venue-primary bg-venue-tertiary text-venue-primary hover:bg-venue-primary hover:text-venue-tertiary",
        venueBrand.brandStyle == "elegant" ? "border-0" : "border-2",
        venueBrand.brandStyle == "playful"
          ? "rounded-full"
          : "rounded-venue-brand",
        small ? "text-sm px-8 py-4" : "text-sm px-16 py-8 font-medium",
      )}
    >
      {name}
    </div>
  );
};
export const PillNavLink = ({ to, name }: { to: string; name: string }) => {
  return (
    <>
      <NavLink to={to}>
        {({ isActive }) => <SectionPill isActive={isActive} name={name} />}
      </NavLink>
    </>
  );
};
const SectionsPillList = ({ menu }: { menu: Menu }) => {
  const sections = getActiveSections(menu);
  const promotions = getPromotions(menu);
  const vouchersEnabled = useSuspenseQuery({
    ...VenueOnlineConfigQueries.current(),
    select: (data) => data.onlineVoucherSales,
  }).data;
  return (
    <div className="flex">
      <div className="flex flex-wrap gap-x-8 gap-y-12">
        {promotions.length > 0 && <PillNavLink to={"/"} name={"Promotions"} />}
        {sections.map((section) => (
          <PillNavLink
            key={section.uuid}
            to={section.uuid}
            name={section.name}
          />
        ))}
        {vouchersEnabled && <PillNavLink to={"vouchers"} name={"Vouchers"} />}
      </div>
    </div>
  );
};

export const VenueWelcome = () => {
  const venue = useVenue();

  return (
    <div className="space-y-12 md:flex md:flex-1 md:items-center md:space-x-16 md:space-y-0">
      {hasLogoImage(venue) && (
        <div className="mx-auto w-96 md:mx-0 md:text-right">
          <Link
            to={`/`}
            className="mx-auto block w-full max-w-[96px] space-y-8 text-center"
          >
            <LogoImage />
          </Link>
        </div>
      )}
      <div className="space-y-16 md:flex-1">
        <div className="text-center text-xl font-extrabold text-venue-primary">
          <Trans
            i18nKey="mainPage.welcomeToVenue"
            values={{ venueName: venue.name }}
            shouldUnescape
          />
        </div>
        <div className="text-sm mx-auto whitespace-pre-wrap font-normal">
          {venue.venueBrand.description}
        </div>
      </div>
    </div>
  );
};

const MenuFilter = ({
  searchTerm,
  onSearchChange,
}: {
  searchTerm?: string;
  onSearchChange: (newTerm: string) => void;
}) => {
  return (
    <div className="space-y-8">
      <SearchField searchTerm={searchTerm} onChange={onSearchChange} />
    </div>
  );
};

const MainPage = ({ isRootPage }: MainPageProps) => {
  const menu = useFetchMenu();

  const isLayoutPage = !isRootPage;
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const onSearchChange = (newTerm: string) => {
    setSearchTerm(newTerm);
  };

  return (
    <RecordFeatureView featureName="Online / Menu">
      <div
        className={classNames("relative space-x-48 pb-96 md:flex md:py-32", {
          "py-16": isRootPage,
        })}
      >
        <div className={classNames("md:flex-1", { "space-y-24": isRootPage })}>
          <div
            className={classNames("flex-1 space-y-24 md:flex md:space-x-32", {
              "hidden md:flex": isLayoutPage,
            })}
          >
            <div className="space-y-24 md:w-0 md:min-w-0 md:flex-1 md:space-y-32">
              {!searchTerm && (
                <>
                  <VenueWelcome />
                  <SectionsPillList menu={menu} />
                  <OnlineOrderingDisabled />
                </>
              )}
              {isRootPage && (
                <div className="md:hidden">
                  <MenuFilter
                    searchTerm={searchTerm}
                    onSearchChange={onSearchChange}
                  />
                </div>
              )}
            </div>
          </div>
          {searchTerm ? (
            <SearchResults
              resetSearch={() => setSearchTerm("")}
              menu={menu}
              searchTerm={searchTerm}
            />
          ) : (
            <Outlet />
          )}
        </div>
        <div
          className={classNames(
            "hidden max-h-screen min-h-0 space-y-24 self-start py-32 md:sticky md:top-0 md:block md:h-screen md:max-w-sm md:flex-1 md:flex-col",
            {},
          )}
        >
          <div className="flex-none">
            <MenuFilter
              searchTerm={searchTerm}
              onSearchChange={onSearchChange}
            />
          </div>
          <div className="flex-1">
            <SideBarCart />
          </div>
        </div>
      </div>
    </RecordFeatureView>
  );
};

export default MainPage;

const ScrollIntoView = ({
  parentRef,
  children,
}: {
  parentRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref?.current && parentRef?.current) {
      const parentNode = parentRef?.current;
      parentNode.scrollTop = ref.current.offsetTop;
    }
  }, []);
  return (
    <div
      className="scroll-smooth"
      style={{ scrollMarginTop: "-400px" }}
      ref={ref}
    >
      {children}
    </div>
  );
};
const SideBarCart = () => {
  const { t } = useTranslation("onsite");
  const isWishList = getIsWishlist(useSession(), useVenue());
  const orderingEnabled = getVenueHasMobileOrdering(useVenue());
  const { cart } = useSession();
  const totalOrderPrice = getCartTotal(cart);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  return (
    <div className="">
      <div
        ref={scrollContainerRef}
        className="h-[85vh] min-h-0 space-y-24 overflow-y-auto scroll-smooth rounded-venue-brand border-[6px] border-venue-tertiary px-12"
      >
        <h3 className="sticky top-0 z-10 bg-venue-secondary py-12 text-center text-2xl font-bold">
          {orderingEnabled ? t("cart.yourOrder") : t("cart.yourWishlist")}
        </h3>
        <div className="space-y-12">
          <div className="-mx-2 space-y-12 overflow-y-auto p-2">
            {cart.items.length > 0 ? (
              cart.items.map((cartItem) => (
                <ScrollIntoView
                  parentRef={scrollContainerRef}
                  key={cartItem.id}
                >
                  <PendingCartItem key={cartItem.id} cartItem={cartItem} />
                </ScrollIntoView>
              ))
            ) : (
              <div className="py-24 text-center text-lg font-medium">
                {t("cart.noPendingOrders")}
              </div>
            )}
          </div>
        </div>
        <OrderedOrderItems />

        <div className="sticky bottom-0 space-y-16 bg-venue-secondary py-12">
          <PriceLine
            label={t("cart.total")}
            value={<Price showCurrency value={totalOrderPrice} />}
          />
          <LinkButton primary to="/your-order/pending">
            {isWishList ? t("mainPage.yourWishlist") : t("mainPage.yourOrder")}
          </LinkButton>
        </div>
      </div>
    </div>
  );
};
